import React, { useContext } from "react";
import { MenuItem, DialogActions, Grid } from "@mui/material";
import { FormattedMessage } from "react-intl";
import type { DateOrTimeViewWithMeridiem, PickerRangeValue } from "@mui/x-date-pickers/internals";
import { type PickersActionBarProps, usePickerContext, usePickerRangePositionContext } from "@mui/x-date-pickers-pro";

import { DateTimeRangeTimePickerContext } from "../contexts";

interface DateTimeRangePickerActionBarProps extends PickersActionBarProps {
	readonly customActions?: ("start" | "end" | "selectTime")[];
}

export const DateTimeRangeActionBar: React.FC<DateTimeRangePickerActionBarProps> = (props) => {
	const { actions, customActions, ...dialogActionProps } = props;
	const { setOpen } = useContext(DateTimeRangeTimePickerContext);

	const { setRangePosition, rangePosition } = usePickerRangePositionContext();
	const { clearValue, setValueToToday, acceptValueChanges, cancelValueChanges, goToNextStep, hasNextStep } =
		usePickerContext<PickerRangeValue, DateOrTimeViewWithMeridiem, string | null>();

	const customMenuItems = customActions?.map((actionType) => {
		switch (actionType) {
			case "start":
				return (
					<MenuItem
						onClick={() => {
							setRangePosition("start");
						}}
						key={actionType}
						sx={{
							textDecoration: rangePosition === "start" ? "underline" : "none",
							"&:hover": {
								textDecoration: rangePosition === "start" ? "underline" : "none",
							},
						}}
						data-testid="select-start-time-action"
					>
						<FormattedMessage id="date-time.start-time" defaultMessage="Start Time" />
					</MenuItem>
				);
			case "end":
				return (
					<MenuItem
						onClick={() => {
							setRangePosition("end");
						}}
						key={actionType}
						sx={{
							textDecoration: rangePosition === "end" ? "underline" : "none",
							"&:hover": {
								textDecoration: rangePosition === "end" ? "underline" : "none",
							},
						}}
						data-testid="select-end-time-action"
					>
						<FormattedMessage id="date-time.end-time" defaultMessage="End Time" />
					</MenuItem>
				);

			case "selectTime":
				return (
					<MenuItem
						onClick={() => {
							setOpen(true);
						}}
						key={actionType}
						data-testid="select-time-action"
					>
						<FormattedMessage id="date-time.select-time" defaultMessage="Select Time" />
					</MenuItem>
				);
			default:
				return null;
		}
	});

	const menuItems = actions?.map((actionType) => {
		switch (actionType) {
			case "clear":
				return (
					<MenuItem
						onClick={() => {
							clearValue();
						}}
						key={actionType}
					>
						<FormattedMessage id="date-time.clear" defaultMessage="Clear" />
					</MenuItem>
				);
			case "cancel":
				return (
					<MenuItem
						onClick={() => {
							cancelValueChanges();
						}}
						key={actionType}
					>
						<FormattedMessage id="date-time.cancel" defaultMessage="Cancel" />
					</MenuItem>
				);
			case "accept":
				return (
					<MenuItem
						onClick={() => {
							acceptValueChanges();
						}}
						key={actionType}
					>
						<FormattedMessage id="date-time.ok" defaultMessage="OK" />
					</MenuItem>
				);
			case "today":
				return (
					<MenuItem
						onClick={() => {
							setValueToToday();
						}}
						key={actionType}
					>
						<FormattedMessage id="date-time.today" defaultMessage="Today" />
					</MenuItem>
				);
			case "next":
				return (
					<MenuItem
						onClick={() => {
							goToNextStep();
						}}
						key={actionType}
					>
						<FormattedMessage id="date-time.next" defaultMessage="Next" />
					</MenuItem>
				);

			case "nextOrAccept":
				if (hasNextStep) {
					return (
						<MenuItem
							onClick={() => {
								goToNextStep();
							}}
							key={actionType}
						>
							<FormattedMessage id="date-time.next" defaultMessage="Next" />
						</MenuItem>
					);
				}

				return (
					<MenuItem
						onClick={() => {
							acceptValueChanges();
						}}
						key={actionType}
					>
						<FormattedMessage id="date-time.ok" defaultMessage="OK" />
					</MenuItem>
				);
			default:
				return null;
		}
	});

	if (customMenuItems === undefined && menuItems === undefined) {
		return null;
	}

	return (
		<DialogActions {...dialogActionProps}>
			<Grid container justifyContent="space-between" sx={{ width: "100%" }}>
				<Grid container>{customMenuItems}</Grid>
				<Grid container>{menuItems}</Grid>
			</Grid>
		</DialogActions>
	);
};
